import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import { getCurSiteId, callApi } from '../lib/Common_funcs';
import { useAuth } from '../lib/StAuthGiver';
import CmsContext from '../lib/CmsContext';
import Spinner from 'react-bootstrap/Spinner';
import {
    FaArrowLeft,
    FaDatabase,
    FaFileAlt,
    FaMagic,
    FaBox,
    FaUsers,
    FaQuestionCircle,
    FaComments,
    FaCalendarAlt
} from 'react-icons/fa';
import '../StCmsLayout.css';

const StCmsLayout = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { loginAction, logOut } = useAuth();
    const curSiteId = getCurSiteId();

    const [schemas, setSchemas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const isMounted = useRef(false);

    useEffect(() => {
        if (isMounted.current) return;
        isMounted.current = true;
        fetchSchemas(true);
    }, []);

    const fetchSchemas = async (initialLoad = false) => {
        try {
            if (initialLoad) setLoading(true);
            else setRefreshing(true);
            const url = `/sites/${curSiteId}/cms/schemas`;
            const rcJson = await callApi(url, 'GET', {}, {});

            if (rcJson.disposition === 'logout') {
                logOut();
                return;
            }

            if (rcJson.httpStatus === '200' || rcJson.httpStatus === 200) {
                const schemasData = rcJson.respBody.data?.schemas || rcJson.respBody.schemas || [];
                setSchemas(schemasData);

                if (rcJson.respBody?.etac) {
                    loginAction(null, rcJson.respBody.etac);
                }
            }
        } catch (err) {
            console.log(err);
        } finally {
            if (initialLoad) setLoading(false);
            else setRefreshing(false);
        }
    };

    const getSchemaIcon = (schemaId) => {
        const icons = {
            'blog': <FaFileAlt />,
            'products': <FaBox />,
            'team': <FaUsers />,
            'faq': <FaQuestionCircle />,
            'testimonials': <FaComments />,
            'events': <FaCalendarAlt />
        };
        return icons[schemaId] || <FaDatabase />;
    };

    const getSchemaRoute = (schemaId) => {
        if (schemaId === 'blog') return `/${curSiteId}/cms/blog`;
        return `/${curSiteId}/cms/entries/${schemaId}`;
    };

    const isActiveRoute = (path) => {
        return location.pathname.includes(path);
    };

    const exitCmsMode = () => {
        navigate(`/${curSiteId}/home`);
    };

    // Context value to share with child pages
    const cmsContextValue = {
        schemas,
        schemasLoading: loading,
        schemasRefreshing: refreshing,
        refreshSchemas: () => fetchSchemas(false),
    };

    return (
        <CmsContext.Provider value={cmsContextValue}>
            <div className="cms-layout">
                {/* CMS Left Panel - Schemas */}
                <aside className="cms-schemas-panel">
                    <div className="cms-panel-header">
                        <button className="cms-back-button" onClick={exitCmsMode} title="Back to Dashboard">
                            <FaArrowLeft />
                        </button>
                        <div className="cms-logo">
                            <FaDatabase className="cms-logo-icon" />
                            <span>CMS</span>
                        </div>
                    </div>

                    {/* Settings Section - AT THE TOP */}
                    <div className="cms-schemas-section">
                        <div className="cms-section-header">
                            <span className="cms-section-title">Settings</span>
                        </div>
                        <ul className="cms-schema-list">
                            <li
                                className={`cms-schema-item cms-settings-item ${isActiveRoute('/cms/schemas') ? 'active' : ''}`}
                                onClick={() => navigate(`/${curSiteId}/cms/schemas`)}
                            >
                                <span className="cms-schema-icon"><FaDatabase /></span>
                                <span className="cms-schema-name">Content Types</span>
                            </li>
                            {/* AI Settings — coming soon
                            <li
                                className={`cms-schema-item cms-settings-item ${isActiveRoute('/cms/brand') ? 'active' : ''}`}
                                onClick={() => navigate(`/${curSiteId}/cms/brand`)}
                            >
                                <span className="cms-schema-icon"><FaMagic /></span>
                                <span className="cms-schema-name">AI Settings</span>
                            </li>
                            */}
                        </ul>
                    </div>

                    {/* Content Types Section - BELOW SETTINGS */}
                    <div className="cms-schemas-section">
                        <div className="cms-section-header">
                            <span className="cms-section-title">Content</span>
                        </div>

                        {loading ? (
                            <div className="cms-loading">
                                <Spinner animation="border" size="sm" style={{ color: 'var(--accent-primary)' }} />
                            </div>
                        ) : (
                            <ul className="cms-schema-list">
                                {schemas.map((schema) => (
                                    <li
                                        key={schema.schema_id}
                                        className={`cms-schema-item ${isActiveRoute(`/cms/${schema.schema_id === 'blog' ? 'blog' : `entries/${schema.schema_id}`}`) ? 'active' : ''}`}
                                        onClick={() => navigate(getSchemaRoute(schema.schema_id))}
                                    >
                                        <span className="cms-schema-icon">
                                            {getSchemaIcon(schema.schema_id)}
                                        </span>
                                        <span className="cms-schema-name">{schema.display_name}</span>
                                    </li>
                                ))}
                                {schemas.length === 0 && (
                                    <li className="cms-schema-empty">
                                        No content types yet
                                    </li>
                                )}
                            </ul>
                        )}
                    </div>

                    {/* AI Credits Footer — coming soon
                    <div className="cms-schemas-section cms-settings-section">
                        <div className="cms-ai-credits">
                            <FaMagic className="cms-ai-icon" />
                            <span>AI Credits</span>
                            <span className="cms-credits-value">45/100</span>
                        </div>
                    </div>
                    */}
                </aside>

                {/* CMS Main Content */}
                <main className="cms-main-content">
                    <Outlet />
                </main>
            </div>
        </CmsContext.Provider>
    );
};

export default StCmsLayout;
